import { MouseEvent, useMemo, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { GridColDef, GridRowParams, MuiEvent } from "@mui/x-data-grid";
import IotVisionChip from "components/common/IotVisionChip";
import IotVisionTable from "components/common/IotVisionTable";
import IotVisionDialog from "components/dialogs/IotVisionDialog";
import { getScheduleStatusDisplay, getScheduleTypeDisplay } from "configs/schedules";
import { EM_DASH, NO_END_LABEL } from "constants/schedule-monitor.constants";
import { IScheduleRowViewModel } from "types/schedule";
import { formatInDisplayTimezone, resolveDisplayTimezoneAbbreviation } from "utils/schedule-date-format";
import ScheduleCursorFooter from "./schedule-cursor-footer";
import ScheduleDetailModal from "./schedule-detail-modal";

const displayOrDash = (value: string | number | null | undefined): string | number =>
    value === null || value === undefined || value === "" ? EM_DASH : value;

const TIMEZONE_ABBREVIATION = resolveDisplayTimezoneAbbreviation();

export const DateTimeCell = ({
    date,
    emptyLabel = EM_DASH,
}: {
    date: Date | null;
    emptyLabel?: string;
}): JSX.Element => {
    if (!date || Number.isNaN(date.getTime())) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyLabel}
            </Typography>
        );
    }
    return (
        <Box>
            <Typography variant="body2">{formatInDisplayTimezone(date, "dd MMM yyyy")}</Typography>
            <Typography variant="caption" color="text.secondary">
                {formatInDisplayTimezone(date, "HH:mm")} {TIMEZONE_ABBREVIATION}
            </Typography>
        </Box>
    );
};

export const ScheduleStatusChip = ({ status }: { status: string }): JSX.Element => {
    const display = getScheduleStatusDisplay(status);
    return (
        <IotVisionChip
            label={display.label.toUpperCase()}
            size="small"
            sx={{
                minWidth: "auto",
                backgroundColor: display.backgroundColor,
                color: display.color,
                border: `1px solid ${display.color}`,
                height: "24px",
                letterSpacing: "0.5px",
                fontWeight: 600,
                "& .MuiChip-label": {
                    padding: "0 10px",
                    display: "flex",
                    justifyContent: "center",
                },
            }}
        />
    );
};

export const DataViewDialog = ({
    open,
    title,
    data,
    onClose,
}: {
    open: boolean;
    title: string;
    data: Record<string, unknown> | null;
    onClose: () => void;
}): JSX.Element => (
    <IotVisionDialog open={open} title={title} onClose={onClose}>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "13px" }}>
            {JSON.stringify(data, null, 2)}
        </pre>
    </IotVisionDialog>
);

interface IScheduleTableProps {
    statusLabel: string;
    typeLabel: string;
    rows: IScheduleRowViewModel[];
    /** Schedule id -> derived status. Falls back to the row's API-reported status when absent (e.g. still loading). */
    derivedStatusById: Record<string, string>;
    /** Schedule id -> whether a next task is queued. `null` when unknown or not applicable (e.g. not active). */
    hasNextTaskById: Record<string, boolean | null>;
    total: number;
    loading: boolean;
    error: string | null;
    hasPrevious: boolean;
    hasNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
}

const ScheduleTable = ({
    statusLabel,
    typeLabel,
    rows,
    derivedStatusById,
    hasNextTaskById,
    total,
    loading,
    error,
    hasPrevious,
    hasNext,
    onPrevious,
    onNext,
}: IScheduleTableProps): JSX.Element => {
    const [selectedData, setSelectedData] = useState<Record<string, unknown> | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<IScheduleRowViewModel | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const handleRowClick = (
        params: GridRowParams<IScheduleRowViewModel>,
        event: MuiEvent<MouseEvent<HTMLElement>>,
    ): void => {
        // Interactive cell content (e.g. the Data "{} view" button) handles its own click -
        // ignore here so the row doesn't also open the detail modal underneath it.
        if ((event.target as HTMLElement).closest("button, a, [role='button']")) return;
        setSelectedRow(params.row);
        setDetailOpen(true);
    };

    const columns: GridColDef<IScheduleRowViewModel>[] = useMemo(
        () => [
            {
                field: "typeLabel",
                headerName: "Type",
                flex: 1.4,
                minWidth: 200,
                sortable: true,
                disableColumnMenu: true,
                renderCell: ({ row }) => {
                    const { icon: TypeIcon, iconColor } = getScheduleTypeDisplay(row.typeCode);
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TypeIcon sx={{ fontSize: 20, color: iconColor }} />
                            <Box>
                                <Typography variant="body1" fontWeight={600} lineHeight={1.3}>
                                    {row.typeLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {row.typeCode}
                                </Typography>
                            </Box>
                        </Box>
                    );
                },
            },
            {
                field: "status",
                headerName: "Status",
                flex: 1,
                minWidth: 160,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) => (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ScheduleStatusChip status={derivedStatusById[row.id] ?? row.status} />
                        {hasNextTaskById[row.id] === false ? (
                            <IotVisionChip
                                icon={<WarningAmberOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                                label="No next task"
                                size="small"
                                variant="filled"
                                sx={{
                                    minWidth: "auto",
                                    backgroundColor: "transparent",
                                    color: "error.main",
                                    height: "24px",
                                    fontWeight: 600,
                                    "& .MuiChip-icon": {
                                        color: "error.main",
                                    },
                                }}
                            />
                        ) : null}
                    </Box>
                ),
            },
            {
                field: "startTime",
                headerName: "Start",
                flex: 1,
                minWidth: 140,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) => <DateTimeCell date={row.startTime} />,
            },
            {
                field: "endTime",
                headerName: "End",
                flex: 1,
                minWidth: 140,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) => <DateTimeCell date={row.endTime} emptyLabel={NO_END_LABEL} />,
            },
            {
                field: "repeats",
                headerName: "Repeats",
                flex: 1,
                minWidth: 120,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) =>
                    row.repeats ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <RepeatOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="body2">{row.repeats}</Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2">{EM_DASH}</Typography>
                    ),
            },
            {
                field: "modifier",
                headerName: "Modifier",
                flex: 1,
                minWidth: 120,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) => displayOrDash(row.modifier),
            },
            {
                field: "createdAt",
                headerName: "Created",
                flex: 1,
                minWidth: 140,
                sortable: true,
                disableColumnMenu: true,
                renderCell: ({ row }) => <DateTimeCell date={row.createdAt} />,
            },
            {
                field: "data",
                headerName: "Data",
                flex: 0.8,
                minWidth: 110,
                sortable: false,
                disableColumnMenu: true,
                renderCell: ({ row }) =>
                    row.data ? (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setSelectedData(row.data);
                                setDialogOpen(true);
                            }}
                        >
                            {"{} view"}
                        </Button>
                    ) : (
                        EM_DASH
                    ),
            },
        ],
        [derivedStatusById, hasNextTaskById],
    );

    const Footer = useMemo(
        () =>
            function ScheduleTableFooter(): JSX.Element {
                return (
                    <ScheduleCursorFooter
                        count={rows.length}
                        total={total}
                        statusLabel={statusLabel}
                        typeLabel={typeLabel}
                        onPrevious={onPrevious}
                        onNext={onNext}
                        hasPrevious={hasPrevious && !loading}
                        hasNext={hasNext && !loading}
                    />
                );
            },
        [rows.length, total, statusLabel, typeLabel, onPrevious, onNext, hasPrevious, hasNext, loading],
    );

    return (
        <>
            {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}
            <IotVisionTable
                rows={rows}
                columns={columns}
                rowCount={rows.length}
                loading={loading}
                hideFooterPagination
                hideFooterSelectedRowCount
                disableColumnSelector
                components={{ Footer }}
                onRowClick={handleRowClick}
                sx={{
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "grey.100",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        fontWeight: 600,
                        color: "text.secondary",
                    },
                    "& .MuiDataGrid-row": {
                        cursor: "pointer",
                    },
                }}
            />
            <DataViewDialog
                open={dialogOpen}
                title="Schedule data"
                data={selectedData}
                onClose={() => {
                    setDialogOpen(false);
                    setSelectedData(null);
                }}
            />
            <ScheduleDetailModal row={selectedRow} open={detailOpen} onClose={() => setDetailOpen(false)} />
        </>
    );
};

export default ScheduleTable;
